import { computed, DestroyRef, ElementRef, inject, Injector, runInInjectionContext, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MediaAssetErrorCode, MediaAssetsHelper } from '@common/media/shared';
import { takeWhileAlive } from '@core/frontend-shared';
import { catchError, combineLatest, distinctUntilChanged, from, map, Subject, switchMap, throwError } from 'rxjs';
import { MediaAssetProvidersFrontendService } from '../../provider';
import { arrayUnique, uuid } from '@core/shared/utils';
import { TimelineItemAdded, TimelineItemRemoved, TimelineItemsUpdated, TimelineItemUpdated, TimelineViewChanged } from './timeline-events';
import { INSERT_ITEM_LENGTH, MIN_ITEM_LENGTH, ZOOM_SPEED } from './constants';
import * as i0 from "@angular/core";
export let TimelineService = /*#__PURE__*/(() => {
  class TimelineService {
    constructor() {
      this.mediaProviders = inject(MediaAssetProvidersFrontendService);
      this.destroyRef = inject(DestroyRef);
      this.injector = inject(Injector);
      // main "inputs" 
      // pass a MediaAssetItem to work with, configurated from component that uses the timeline, e.g. MediaAssetSubtitlesManagerComponent
      this.asset = signal(null, ...(ngDevMode ? [{
        debugName: "asset"
      }] : /* istanbul ignore next */[]));
      // every used track must be registered so that timeline knows which tracks to offer/create even if no items exist
      this.tracks = signal([], ...(ngDevMode ? [{
        debugName: "tracks"
      }] : /* istanbul ignore next */[]));
      this.provider = computed(() => {
        const asset = this.asset();
        if (!asset) return null;
        return this.mediaProviders.getProvider(asset);
      }, ...(ngDevMode ? [{
        debugName: "provider"
      }] : /* istanbul ignore next */[]));
      this.enablePlayer = signal(true, ...(ngDevMode ? [{
        debugName: "enablePlayer"
      }] : /* istanbul ignore next */[]));
      this.assetInfo = signal(null, ...(ngDevMode ? [{
        debugName: "assetInfo"
      }] : /* istanbul ignore next */[]));
      this.duration = computed(() => {
        const info = this.assetInfo();
        return info?.details?.duration || null;
      }, ...(ngDevMode ? [{
        debugName: "duration"
      }] : /* istanbul ignore next */[]));
      // the zoom value is the count of pixels that one second takes up.
      this.availWidth = signal(1000, ...(ngDevMode ? [{
        debugName: "availWidth"
      }] : /* istanbul ignore next */[]));
      this.pxPerSec = signal(50, ...(ngDevMode ? [{
        debugName: "pxPerSec"
      }] : /* istanbul ignore next */[]));
      // the pxPerSec to take up only the available width
      this.pxPerSecMin = computed(() => {
        const duration = this.duration();
        const availWidth = this.availWidth();
        const timelinePadding = this.timelinePadding();
        return (availWidth - timelinePadding * 2) / duration;
      }, ...(ngDevMode ? [{
        debugName: "pxPerSecMin"
      }] : /* istanbul ignore next */[]));
      this.pxPerSecCalculated = computed(() => {
        return Math.max(this.pxPerSec(), this.pxPerSecMin());
      }, ...(ngDevMode ? [{
        debugName: "pxPerSecCalculated"
      }] : /* istanbul ignore next */[]));
      this.viewChange$ = combineLatest({
        zoom: toObservable(this.pxPerSecCalculated)
      }).pipe(distinctUntilChanged(), takeWhileAlive(this.destroyRef));
      this.currPositionSec = signal(0, ...(ngDevMode ? [{
        debugName: "currPositionSec"
      }] : /* istanbul ignore next */[]));
      this.timelineHeight = signal(150, ...(ngDevMode ? [{
        debugName: "timelineHeight"
      }] : /* istanbul ignore next */[]));
      this.timelinePadding = signal(40, ...(ngDevMode ? [{
        debugName: "timelinePadding"
      }] : /* istanbul ignore next */[]));
      // unsorted!
      this.items = signal([], ...(ngDevMode ? [{
        debugName: "items"
      }] : /* istanbul ignore next */[]));
      // sorted!
      this.itemsPerTrack = signal(null, ...(ngDevMode ? [{
        debugName: "itemsPerTrack"
      }] : /* istanbul ignore next */[]));
      this.tracksWithItems = computed(() => {
        const itemMap = this.itemsPerTrack();
        return this.tracks().map(track => {
          return {
            ...track,
            items: itemMap.get(track.id)
          };
        });
      }, ...(ngDevMode ? [{
        debugName: "tracksWithItems"
      }] : /* istanbul ignore next */[]));
      this.selection = signal(null, ...(ngDevMode ? [{
        debugName: "selection"
      }] : /* istanbul ignore next */[]));
      this.selectedItem = computed(() => {
        const sel = this.selection();
        if (sel === null) return null;
        const match = this.items().find(i => i.id === sel);
        return match || null;
      }, ...(ngDevMode ? [{
        debugName: "selectedItem"
      }] : /* istanbul ignore next */[]));
      this.isDragging = signal(false, ...(ngDevMode ? [{
        debugName: "isDragging"
      }] : /* istanbul ignore next */[])); // true while dragging an item
      this._processItems = toObservable(this.items).pipe(
      // NOTE: debouncing is no bad idea, but results in ugly UI flickering
      // when dropping an item it would jump back to old position for some ms.
      takeWhileAlive()).subscribe(items => {
        const itemsPerTrack = new Map();
        for (const track of this.tracks()) {
          itemsPerTrack.set(track.id, []);
        }
        for (const item of items) {
          const list = itemsPerTrack.get(item.track);
          if (!list) throw new Error('MediaAssetTimelineItem requested unknown track ID: ' + item.track);
          list.push(item);
        }
        // find and fix overlapping items
        // NOTE: modifiedItems requires some explanation:
        // depending on situation, sanitizeTrackItems can change 1 or 2 items at once.
        // this is because the item after the current one may need an update to,
        // for example to move the start time to a later point to free required space for min length of current item.
        // for that reason, modifiedItems may include any item more than once, which must be filtered then.
        let modifiedItems = [];
        itemsPerTrack.forEach(items => {
          items.sort((a, b) => {
            return a.start - b.start;
          });
          modifiedItems.push(...this.sanitizeTrackItems(items, true));
        });
        modifiedItems = arrayUnique(modifiedItems);
        if (modifiedItems.length) {
          this.updateItems(modifiedItems);
        } else {
          this.itemsPerTrack.set(itemsPerTrack);
        }
      });
      this.playheadX = computed(() => {
        return this.pxPerSecCalculated() * this.currPositionSec() + this.timelinePadding() - 1;
      }, ...(ngDevMode ? [{
        debugName: "playheadX"
      }] : /* istanbul ignore next */[]));
      this.events$ = new Subject();
      this.initialized = false;
    }
    get canvas() {
      return this.canvasRef?.nativeElement;
    }
    get ctx() {
      return this.canvas?.getContext('2d');
    }
    initialize(scrollRef, canvasRef) {
      this.scrollRef = scrollRef;
      this.canvasRef = canvasRef;
      this.initialized = true;
      runInInjectionContext(this.injector, () => {
        combineLatest({
          duration: toObservable(this.duration),
          pxPerSec: toObservable(this.pxPerSecCalculated)
        }).pipe(takeWhileAlive(this.destroyRef)).subscribe(({
          duration,
          pxPerSec
        }) => {
          this.rebuildTimeline(duration, pxPerSec);
        });
        toObservable(this.asset).pipe(takeWhileAlive(this.destroyRef),
        // asset will reload regularly due to saving, but only a change of the asset itself should retrigger fetching details!
        distinctUntilChanged((a, b) => {
          return a.id === b.id;
        }), switchMap(asset => {
          const status = MediaAssetsHelper.checkAssetStatusForErrors(asset, this.mediaProviders);
          if (status.error) {
            return throwError(() => status.error);
          } else {
            const provider = this.mediaProviders.getProvider(asset);
            return from(provider.getAssetDetails(asset)).pipe(map(details => {
              return {
                asset,
                details,
                provider
              };
            }), catchError(err => {
              return throwError(() => MediaAssetErrorCode.NotFound);
            }));
          }
        })).subscribe({
          next: ({
            asset,
            details,
            provider
          }) => {
            this.assetInfo.set({
              error: null,
              preview: provider.getPreviewInfo(asset, true, 'preview'),
              details
            });
          },
          error: error => {
            this.assetInfo.set({
              error
            });
          }
        });
      });
    }
    setIsDragging(state) {
      this.isDragging.set(state);
    }
    selectItem(item) {
      this.selection.set(item ? item.id : null);
    }
    updateItem(itemId, patch, emitEvent = true) {
      let updatedItem;
      const newItems = this.items().map(i => {
        if (i.id !== itemId) return i;
        updatedItem = this.applyItemPatch(i, patch);
        return updatedItem;
      });
      this.items.set(newItems);
      if (emitEvent) this.events$.next(new TimelineItemUpdated(updatedItem));
    }
    applyItemPatch(item, patch) {
      return {
        ...item,
        ...patch,
        data: {
          ...(item.data || {}),
          ...(patch.data || {})
        }
      };
    }
    updateItems(items, emitEvent = true) {
      if (!items.length) return;
      const idMap = new Map();
      items.forEach(item => idMap.set(item.id, item));
      const updatedItems = [];
      const newItems = this.items().map(i => {
        if (idMap.has(i.id)) {
          const item = this.applyItemPatch(i, idMap.get(i.id));
          updatedItems.push(item);
          return item;
        }
        return i;
      });
      this.items.set(newItems);
      if (emitEvent) this.events$.next(new TimelineItemsUpdated(updatedItems));
    }
    updateItemData(itemOrId, data) {
      const id = typeof itemOrId === 'object' ? itemOrId.id : itemOrId;
      this.updateItem(id, {
        data
      }, false);
      return true;
    }
    updateItemLabel(itemOrId, label) {
      const id = typeof itemOrId === 'object' ? itemOrId.id : itemOrId;
      this.updateItem(id, {
        label
      }, false);
      return true;
    }
    addItem(track, start, label, data, emitEvent = true) {
      const id = uuid();
      const newItem = {
        id,
        track,
        start,
        label,
        end: start + INSERT_ITEM_LENGTH,
        data
      };
      this.items.set([...this.items(), newItem]);
      this.selection.set(newItem.id);
      if (emitEvent) this.events$.next(new TimelineItemAdded(newItem));
    }
    deleteItem(item, emitEvent = true) {
      if (this.selection() === item.id) {
        this.selection.set(null);
      }
      const newItems = this.items().filter(i => {
        return i.id !== item.id;
      });
      this.items.set(newItems);
      if (emitEvent) this.events$.next(new TimelineItemRemoved(item));
    }
    // timeline interaction and calculation -----------------------------------------------------------------------------------------------------
    sanitizeTrackItems(items, skipSorting = false) {
      if (items.length === 0) return [];
      const trackId = items[0].track;
      const duration = this.duration();
      const mismatch = items.some(i => i.track !== trackId);
      if (mismatch) throw new Error('sanitizeTrackItems received items belonging to separate tracks!');
      if (!skipSorting) items.sort((a, b) => {
        return a.start - b.start;
      });
      const modifiedItems = [];
      for (let i = 0; i < items.length; i++) {
        const curr = items[i];
        const next = items[i + 1];
        if (curr.end < curr.start + MIN_ITEM_LENGTH) {
          curr.end = curr.start + MIN_ITEM_LENGTH;
          if (!next && curr.end > duration) curr.end = duration;
          modifiedItems.push(curr);
        }
        if (next) {
          if (next.start < curr.end) {
            next.start = curr.end;
            modifiedItems.push(next);
          }
        }
      }
      return modifiedItems;
    }
    getItemAtPosition(time, track) {
      const items = this.itemsPerTrack().get(track);
      for (let i = 0; i < items.length; i++) {
        if (items[i].end <= time) continue;else if (items[i].start >= time) break; // items are sorted by start time, so the first item starting behind given time can safely stop the search.
        else return items[i]; // item ends after time and starts before time => it's a match!
      }
      return null;
    }
    getItemSiblings(item) {
      const items = this.itemsPerTrack().get(item.track);
      const index = items.findIndex(i => i.id === item.id);
      if (index === -1) throw new Error('item not part of specified track!');
      const nextItem = items[index + 1] || null;
      const prevItem = items[index - 1] || null;
      return {
        prevItem,
        nextItem
      };
    }
    rebuildTimeline(duration, pxPerSec) {
      this.assertInitialized();
      const timelinePadding = this.timelinePadding();
      this.canvas.width = duration * pxPerSec + timelinePadding * 2;
      this.canvas.height = this.timelineHeight();
      if (pxPerSec > 20) {
        // every second
        for (let i = 1; i <= duration; i++) {
          if (i % 10 === 0) continue;
          const x = timelinePadding + i * pxPerSec;
          this.drawYCenterLine(x, .2, '#555', 1);
        }
      }
      if (pxPerSec > 5) {
        // every 10th second
        for (let i = 10; i <= duration; i += 10) {
          if (i % 60 === 0) continue;
          const x = timelinePadding + i * pxPerSec;
          this.drawYCenterLine(x, .5, '#aaa', 1, this.getTimeLabel(i));
        }
      }
      for (let i = 0; i <= duration; i += 60) {
        // every minute
        const x = timelinePadding + i * pxPerSec;
        this.drawYCenterLine(x, .75, '#fff', 1, this.getTimeLabel(i));
      }
    }
    getTimeLabel(posSec) {
      const min = Math.floor(posSec / 60);
      const sec = posSec % 60;
      return min + 'm ' + sec + 's';
    }
    drawYCenterLine(x, heightPercent, color, width, label) {
      this.assertInitialized();
      const availHeight = this.timelineHeight() - 20;
      const beginY = availHeight * (0.5 - heightPercent * 0.5);
      const endY = availHeight * (0.5 + heightPercent * 0.5);
      const xPos = x - 0.5 * width;
      this.ctx.beginPath();
      this.ctx.moveTo(xPos, beginY);
      this.ctx.lineTo(xPos, endY);
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = width;
      this.ctx.stroke();
      if (label) {
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'top';
        this.ctx.font = '15px Arial';
        this.ctx.fillStyle = color;
        this.ctx.fillText(label, x, availHeight);
      }
    }
    updateTimelineScrollOffset() {
      this.assertInitialized();
      const pxPerSec = this.pxPerSecCalculated();
      const currTime = this.currPositionSec();
      const availWidthPx = this.availWidth();
      // to ensure the timeline updates before currTime hits min/max edge, we use a pixel-to-seconds calculation 
      const triggerPaddingSec = availWidthPx * 0.15 / pxPerSec;
      const visible = this.getVisibleRange();
      // console.log('allowed range for good ux', availWidthPx, triggerPaddingSec, visibleXMin - triggerPaddingSec, visibleXMax - triggerPaddingSec)
      if (currTime < visible.secondsMin - triggerPaddingSec) {
        this.scrollTo(currTime * pxPerSec);
        return;
      }
      if (currTime > visible.secondsMax - triggerPaddingSec) {
        this.scrollTo(3 * pxPerSec, true);
        return;
      }
    }
    scrollTo(position, relative = false, animated = true) {
      this.assertInitialized();
      // NOTE: In case the timeline is <= 100% width, setting scrollOffset will be ignored.
      // this can happen if zoom is currently changing from minimum (100% width) to something greater.
      // requestAnimationFrame will trigger just in time when UI has recalculated.
      requestAnimationFrame(() => {
        this.emitViewChange();
        const left = !relative ? position : this.getTimelineScrollX() + position;
        const behavior = animated ? 'smooth' : undefined;
        this.scrollRef.nativeElement.scroll({
          top: 0,
          left,
          behavior
        });
      });
    }
    getTimelineScrollX() {
      this.assertInitialized();
      return this.scrollRef.nativeElement.scrollLeft;
    }
    pxPositionToTimelineSeconds(xOffset) {
      this.assertInitialized();
      const timelineXPos = xOffset - this.timelinePadding();
      const wrapperXPos = xOffset - this.getTimelineScrollX();
      const timelineSeconds = timelineXPos / this.pxPerSecCalculated();
      return {
        timelineSeconds,
        offsetX: xOffset,
        wrapperXPos,
        timelineXPos
      };
    }
    timelineSecondsToPxPosition(timelineSeconds) {
      this.assertInitialized();
      const timelineXPos = timelineSeconds * this.pxPerSecCalculated();
      const offsetX = timelineXPos + this.timelinePadding();
      const wrapperXPos = offsetX - this.getTimelineScrollX();
      return {
        timelineSeconds,
        offsetX,
        wrapperXPos,
        timelineXPos
      };
    }
    getVisibleRange() {
      this.assertInitialized();
      const scrollOffset = this.getTimelineScrollX();
      const availWidthPx = this.availWidth();
      const pxPerSec = this.pxPerSecCalculated();
      const pxMin = Math.max(0, 0 - this.timelinePadding() + scrollOffset);
      const secondsMin = pxMin / pxPerSec;
      const secondsRange = availWidthPx / pxPerSec;
      const secondsMax = secondsMin + secondsRange;
      return {
        secondsMin,
        secondsRange,
        secondsMax,
        pxMin,
        pxRange: availWidthPx
      };
    }
    assertInitialized() {
      if (!this.initialized) throw new Error('TimeineService used before initialization!');
    }
    zoomAt(xOffset, zoomIn, factor = ZOOM_SPEED) {
      const focusedPoint = this.pxPositionToTimelineSeconds(xOffset);
      const minZoom = this.pxPerSecMin();
      const newZoom = Math.max(minZoom, this.pxPerSec() * (1 + (zoomIn ? 1 : -1) * factor));
      // const visible = this.getVisibleRange();
      // console.log(focusedPoint, newZoom)
      this.pxPerSec.set(newZoom);
      const samePointInChangedZoom = this.timelineSecondsToPxPosition(focusedPoint.timelineSeconds);
      const offsetCorrection = samePointInChangedZoom.wrapperXPos - focusedPoint.wrapperXPos;
      // console.log(offsetCorrection, focusedPoint, samePointInChangedZoom);
      // NOTE: difficulty here: the ui won't have been updated yet. 
      // In case the timeline is <= 100% width, setting scrollOffset will be ignored.
      // requestAnimationFrame will trigger just in time when UI has recalculated.
      this.scrollTo(offsetCorrection, true, false);
      this.emitViewChange();
      // requestAnimationFrame(()=>{
      // })
    }
    updateAvailWidth(widthPx) {
      this.availWidth.set(widthPx);
      this.emitViewChange();
    }
    emitViewChange() {
      const range = this.getVisibleRange();
      this.events$.next(new TimelineViewChanged(this.pxPerSecCalculated(), range.secondsMin, range.secondsMax));
    }
    static {
      this.ɵfac = function TimelineService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TimelineService)();
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: TimelineService,
        factory: TimelineService.ɵfac
      });
    }
  }
  return TimelineService;
})();